import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Coins } from "lucide-react";
import { Input } from "@/components/ui/input";
import { addToken, removeToken } from "@/lib/api";
import { formatErr } from "@/lib/auth";

const EXAMPLES = [
  { s: "LINK", a: "0x514910771AF9Ca656af840dff83E8264EcF986CA" },
  { s: "UNI", a: "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984" },
  { s: "SHIB", a: "0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE" },
];

export default function TokenManager({ tokens, onChange, user }) {
  const [addr, setAddr] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const t = await addToken(addr);
      toast.success(`Now tracking ${t.symbol} exchange flows`);
      setAddr("");
      onChange();
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const remove = async (sym) => {
    try { await removeToken(sym); toast.success(`${sym} removed`); onChange(); } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <section className="panel space-y-3 p-4" data-testid="token-manager">
      <div className="flex items-center gap-2">
        <Coins className="h-4 w-4 text-[#00E5FF]" />
        <h2 className="font-display text-base font-semibold">Track a new coin</h2>
      </div>
      <p className="text-xs text-slate-400">Paste any Ethereum (ERC-20) token contract. We read its symbol on-chain and start watching its whale moves to and from exchanges.</p>
      <form onSubmit={add} className="flex gap-2">
        <Input data-testid="token-address-input" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x… contract address"
          className="border-[#2E3C54] bg-[#0F141C] font-mono text-xs" />
        <button data-testid="token-add-button" disabled={busy || !addr.trim()}
          className="flex items-center gap-1 rounded-md bg-[#00E5FF] px-3 text-sm font-semibold text-[#080B10] transition-opacity hover:opacity-90 disabled:opacity-40">
          <Plus className="h-4 w-4" /> {busy ? "…" : "Add"}
        </button>
      </form>
      <div className="flex flex-wrap gap-1 font-mono text-[10px] text-slate-500">
        Try:
        {EXAMPLES.map((x) => (
          <button key={x.s} type="button" data-testid={`token-example-${x.s}`} onClick={() => setAddr(x.a)} className="text-[#00E5FF] hover:underline">{x.s}</button>
        ))}
      </div>
      <ul className="space-y-1" data-testid="token-list">
        {tokens.map((t) => (
          <li key={t.address} data-testid={`token-item-${t.symbol}`} className="flex items-center gap-2 rounded-md border border-[#1E2738] px-2.5 py-1.5">
            <span className="font-mono text-xs font-semibold">{t.symbol}</span>
            <span className="truncate text-[11px] text-slate-500">{t.name}</span>
            <span className="ml-auto font-mono text-[11px] text-slate-300">{t.price ? `$${t.price.toPrecision(4)}` : "—"}</span>
            {(user?.role === "admin" || user?.email === t.added_by_email) && (
              <button onClick={() => remove(t.symbol)} data-testid={`token-remove-${t.symbol}`} className="text-slate-500 hover:text-[#FF3D00]"><X className="h-3.5 w-3.5" /></button>
            )}
          </li>
        ))}
        {!tokens.length && <li className="text-xs text-slate-600" data-testid="token-list-empty">No custom coins yet.</li>}
      </ul>
    </section>
  );
}
